<template>
  <nav class="packages-overview-results-navigation">

    <div class="packages-overview-results-navigation__container">

      <Button icon="@/assets/images/icons/filter.svg"
              :class="[
                'packages-overview-results-navigation__button',
                'packages-overview-results-navigation__button--filter',
              ]"
              @click="$emit('openFilters')"
      >
        Filteren
      </Button>

      <SelectButton v-model="sortOrderHandler"
                    :options="sortOrders"
                    icon="@/assets/images/icons/chevron-down.svg"
                    iconPosition="right"
                    valuePrefix="Sorteren op"
                    :class="[
                      'packages-overview-results-navigation__button',
                      'packages-overview-results-navigation__button--sort',
                    ]"
                    />

      <ul class="packages-overview-active-filters">
        <li class="packages-overview-active-filters__item"
            v-for="(filter, key) in activeFilterValues"
            :key="key"
        >

          {{filter.name}}

          <button class="packages-overview-active-filters__item-button"
                  @click="removeActiveFilter(filter.type, filter.id)"
          >
            <InlineSvg src="@/assets/images/icons/x.svg"
                       class="packages-overview-active-filters__item-button-icon"
                       />
          </button>

        </li>
      </ul>

    </div>

  </nav>
</template>

<script>
import _ from 'lodash';
import {
  FILTER_TYPE_ACCOMMODATION_TYPES,
  FILTER_TYPE_AIRPORTS,
  FILTER_TYPE_DEPARTURE_DATE_FROM,
  FILTER_TYPE_DEPARTURE_DATE_TO,
  FILTER_TYPE_DESTINATIONS_SUMMER,
  FILTER_TYPE_DESTINATIONS_WINTER,
  FILTER_TYPE_DURATIONS,
  FILTER_TYPE_EXCURSIONS,
  FILTER_TYPE_HOST_TYPES,
  FILTER_TYPE_PACKAGE_TYPES,
  FILTER_TYPE_PRICE_RANGE,
  FILTER_TYPE_TOUR_GROUPS,
} from '@/constants';

import Button from '@/components/UI/Button.vue';
import InlineSvg from '@/components/UI/InlineSvg.vue';
import SelectButton from '@/components/UI/SelectButton.vue';

export default {
  name: 'ResultsNavigation',

  components: {
    Button,
    InlineSvg,
    SelectButton,
  },

  props: {
    activeFilters: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    sortOrder: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    sortOrders: [
      {
        id: 'default',
        title: 'Populariteit',
        enabled: true,
      },
      {
        id: 'price_asc',
        title: 'Prijs, oplopend',
        enabled: true,
      },
      {
        id: 'price_desc',
        title: 'Prijs, aflopend',
        enabled: true,
      },
    ],
  }),

  computed: {
    activeFilterValues() {
      let activeFilterValues = [];

      Object.keys(this.activeFilters).forEach((type) => {
        switch (type) {
          case FILTER_TYPE_AIRPORTS:
          case FILTER_TYPE_DURATIONS:
          case FILTER_TYPE_PACKAGE_TYPES: {
            activeFilterValues.push(this.getActiveSelectValue(type));
            break;
          }

          case FILTER_TYPE_DEPARTURE_DATE_FROM:
          case FILTER_TYPE_DEPARTURE_DATE_TO: {
            activeFilterValues.push(this.getActiveDateValue(type));
            break;
          }

          case FILTER_TYPE_PRICE_RANGE: {
            activeFilterValues.push(this.getActiveRangeValue(type));
            break;
          }

          case FILTER_TYPE_DESTINATIONS_SUMMER:
          case FILTER_TYPE_DESTINATIONS_WINTER:
          case FILTER_TYPE_EXCURSIONS:
          case FILTER_TYPE_ACCOMMODATION_TYPES:
          case FILTER_TYPE_HOST_TYPES:
          case FILTER_TYPE_TOUR_GROUPS: {
            activeFilterValues = [
              ...activeFilterValues,
              ...this.getActiveCheckboxValues(type),
            ];
            break;
          }

          // no default
        }
      });

      return activeFilterValues;
    },

    sortOrderHandler: {
      get() {
        return this.sortOrder;
      },
      set(sortOrder) {
        this.$emit('update:sortOrder', sortOrder);
      },
    },
  },

  methods: {
    getActiveCheckboxValues(type) {
      return this.filters[type].options
        .filter((option) => this.activeFilters[type].includes(option.id))
        .map((option) => ({
          type,
          name: option.title,
          id: option.id,
        }));
    },

    getActiveDateValue(type) {
      const date = this.activeFilters[type];
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');

      return {
        type,
        name: `${day}-${month}-${date.getFullYear()}`,
        id: null,
      };
    },

    getActiveDestinationValues(type) {
      return [
        ...this.filters[FILTER_TYPE_DESTINATIONS_SUMMER].options,
        ...this.filters[FILTER_TYPE_DESTINATIONS_WINTER].options,
      ]
        .filter((option) => this.activeFilters[type].includes(option.id))
        .map((option) => ({
          type,
          name: option.title,
          id: option.id,
        }));
    },

    getActiveRangeValue(type) {
      const range = this.activeFilters[type];

      return {
        type,
        name: `€ ${range[0]} - € ${range[1]}`,
        id: null,
      };
    },

    getActiveSelectValue(type) {
      const activeOption = this.filters[type].options.find(
        (option) => option.id === this.activeFilters[type],
      );

      return {
        type,
        name: activeOption.title,
        id: null,
      };
    },

    removeActiveFilter(type, id) {
      const activeFilters = _.cloneDeep(this.activeFilters);

      if (id !== null) {
        activeFilters[type].splice(activeFilters[type].indexOf(id), 1);
        if (!activeFilters[type].length) {
          delete activeFilters[type];
        }
      } else {
        delete activeFilters[type];
      }

      this.$emit('update:activeFilters', activeFilters);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../assets/css/helpers/mixins/breakpoints';

  .packages-overview-results-navigation {
    padding: var(--spacing-m) var(--spacing-m) 0;

    @include laptop-and-larger {
      padding: var(--spacing-m) 0 0;
    }

    &__container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 1px solid var(--color-divider);

      @include tablet-and-smaller {
        flex-wrap: wrap;
      }
    }

    &__button {
      justify-content: flex-start;
      flex-shrink: 0;
      margin-bottom: var(--spacing-m);

      @include mobile-only {
        width: calc(50% - (var(--spacing-m) / 2));
      }

      &--filter {
        @include laptop-and-larger {
          display: none;
        }
      }

      &--sort {
        margin-left: auto;
      }

      &:nth-child(odd) {
        margin-right: var(--spacing-m);
      }
    }
  }

  .packages-overview-active-filters {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: -5px -5px calc(var(--spacing-m) - 5px);
    font-size: 14px;
    font-weight: 600;
    color: var(--color-secondary);
    list-style: none;

    @include laptop-and-larger {
      order: -1;
      margin-top: 0;
      margin-bottom: var(--spacing-m);
      margin-right: var(--spacing-m);
    }

    &:empty {
      display: none;
    }

    &__item {
      display: flex;
      align-items: center;
      padding-left: 10px;
      margin: 5px;
      border: 1px solid var(--color-secondary);
      white-space: nowrap;

      &-button {
        padding: 8px 10px;
        background-color: transparent;
        color: inherit;

        &-icon {
          display: block;
          width: 1em;
          height: 1em;
          color: inherit;
        }
      }
    }
  }
</style>
